import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { useLockBodyScroll } from '@/shared/hooks';
import { EASE } from '@/shared/motion/transitions';
import { IconButton } from './IconButton';
import { cn } from './utils';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  /** Footer area, typically a row of buttons. */
  footer?: ReactNode;
  /** Hide the close button in the top-right. */
  hideCloseButton?: boolean;
  /** Disable closing on scrim click / Escape. */
  staticBehavior?: boolean;
  size?: ModalSize;
  children?: ReactNode;
  className?: string;
}

const SIZE_CLS: Record<ModalSize, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[680px]',
  xl: 'max-w-[860px]',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  hideCloseButton,
  staticBehavior,
  size = 'md',
  children,
  className,
}: ModalProps) {
  useLockBodyScroll(open);

  useEffect(() => {
    if (!open || staticBehavior) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, staticBehavior, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => !staticBehavior && onClose()}
            className="absolute inset-0 backdrop-blur-sm bg-[radial-gradient(80%_80%_at_50%_40%,rgba(48,38,22,.26),rgba(26,20,12,.44))]"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.25, ease: EASE }}
            style={{ boxShadow: '0 32px 90px rgba(48,38,22,.28), var(--inset-hi)' }}
            className={cn(
              'relative w-full overflow-hidden flex flex-col',
              'bg-[rgba(250,252,255,0.42)] backdrop-blur-[56px] backdrop-saturate-150 border border-white/55',
              'rounded-t-[24px] sm:rounded-[30px] max-h-[88vh] sm:max-h-[85vh]',
              SIZE_CLS[size],
              className,
            )}
          >
            {(title || !hideCloseButton) && (
              <header className="flex items-start justify-between gap-3 px-5 py-4 bg-white/28 border-b border-[rgba(112,94,64,.10)]">
                <div className="min-w-0">
                  {title && (
                    <h3 className="font-headline text-[18px] text-ink-900 leading-tight">
                      {title}
                    </h3>
                  )}
                  {description && (
                    <p className="mt-1 text-[12.5px] text-ink-500 leading-snug">{description}</p>
                  )}
                </div>
                {!hideCloseButton && (
                  <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    size="sm"
                    className="shrink-0 rounded-full bg-[rgba(112,94,64,.10)]"
                  >
                    <X size={14} />
                  </IconButton>
                )}
              </header>
            )}
            <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
            {footer && (
              <footer className="px-5 py-3 border-t border-ink-100 flex items-center justify-end gap-2">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
